/**
 * SMS notification preferences — phone number + opt-out toggle.
 * Reusable across all role-specific Settings pages.
 */
import React, { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MessageSquare, CheckCircle2, AlertCircle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function SmsPreferences() {
  const { user, setUser } = useAuth();
  const [phone, setPhone] = useState("");
  const [optOut, setOptOut] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (user) {
      setPhone(user.phone || "");
      setOptOut(!!user.sms_opt_out);
    }
  }, [user]);

  const save = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const { data } = await api.post("/me/sms-preferences", {
        sms_opt_out: optOut,
        phone: phone.trim() || null,
      });
      // Refresh user via /auth/me so updated phone surfaces everywhere
      const me = await api.get("/auth/me");
      setUser?.(me.data);
      toast.success(data.sms_opt_out ? "SMS notifications turned off" : "SMS preferences updated");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not update SMS preferences");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/me/sms-preferences/test");
      toast.success(`Test SMS sent to ${data.to} — check your phone in a few seconds.`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not send test SMS");
    } finally {
      setTesting(false);
    }
  };

  // The test-SMS CTA is only meaningful AFTER the user has saved a phone and
  // is opted-in. We compare the persisted state on `user` (not local state)
  // so people don't get a working button until they've actually saved.
  const canTest = !!user?.phone && !user?.sms_opt_out;

  return (
    <Card className="rounded-xl shadow-soft border-slate-200" data-testid="sms-preferences-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-5">
          <span className="inline-grid place-items-center size-9 rounded-md bg-slate-900 text-white">
            <MessageSquare className="size-4" strokeWidth={1.7} />
          </span>
          <div>
            <h2 className="text-lg font-semibold text-slate-900 leading-none">SMS notifications</h2>
            <p className="text-xs text-slate-500 mt-1">
              Receive transactional SMS for purchases, top-ups, payouts and contract signings.
            </p>
          </div>
        </div>

        <form onSubmit={save} className="grid gap-4 max-w-xl" data-testid="sms-prefs-form">
          <div>
            <Label htmlFor="sms-phone">Mobile number</Label>
            <Input
              id="sms-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+44 7700 900000"
              data-testid="sms-phone-input"
            />
            <p className="text-[11px] text-slate-500 mt-1.5">
              International format (E.164). UK numbers can omit the leading 0 and start with +44.
            </p>
          </div>

          <label className="flex items-start gap-2.5 cursor-pointer" data-testid="sms-optout-label">
            <input
              type="checkbox"
              checked={optOut}
              onChange={(e) => setOptOut(e.target.checked)}
              className="mt-1 size-4 rounded border-slate-300 text-[#0055FF] focus:ring-[#0055FF]"
              data-testid="sms-optout-checkbox"
            />
            <span className="text-[13px] text-slate-700 leading-relaxed">
              <span className="font-medium text-slate-900">Opt out of all SMS notifications.</span>
              <br />
              <span className="text-slate-500">Critical security alerts (e.g. password resets) may still be sent regardless.</span>
            </span>
          </label>

          {!optOut && !phone.trim() && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[13px] text-amber-900 flex items-start gap-2">
              <AlertCircle className="size-4 mt-0.5 shrink-0" />
              <span>Add a mobile number above to start receiving SMS notifications.</span>
            </div>
          )}

          <div className="flex items-center justify-between gap-2 flex-wrap">
            {/* iter89: instant "send me a test" verification — only enabled
                once a phone has been saved AND opt-in is on. */}
            <button
              type="button"
              onClick={sendTest}
              disabled={!canTest || testing}
              className="inline-flex items-center gap-1.5 rounded-full px-4 h-9 text-[12.5px] font-medium border border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="sms-prefs-test-btn"
              title={canTest ? "Send a test SMS to your saved number" : "Save a phone number and enable SMS first"}
            >
              {testing ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
              Send me a test SMS
            </button>
            <Button disabled={busy} type="submit" className="rounded-full" data-testid="sms-prefs-save">
              {busy ? "Saving…" : <>Save preferences <CheckCircle2 className="size-4" /></>}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
